import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { SESClient, SendEmailCommand } from "@aws-sdk/client-ses";

let sesClient: SESClient | null = null;

const INQUIRY_RECIPIENTS = [
  "zahid@qtfoods.co.jp",
  "omar@qtfoods.co.jp",
  "sales@qtfoods.co.jp",
];
const INQUIRY_SENDER = "QT Foods Inquiry <zahid@qtfoods.co.jp>";

const itemSchema = z.object({
  productHandle: z.string().min(1).max(200),
  productName: z.string().min(1).max(300),
  categoryName: z.string().max(200).optional(),
  quantity: z.number().int().min(1).max(100000),
  note: z.string().max(1000).optional(),
});

const inquirySchema = z.object({
  company: z.string().min(1).max(200),
  contactName: z.string().min(1).max(200),
  email: z.string().email().max(200),
  phone: z.string().max(60).optional(),
  country: z.string().max(100).optional(),
  message: z.string().max(2000).optional(),
  website: z.string().max(200).optional(), // honeypot
  items: z.array(itemSchema).min(1).max(200),
});

export const sendInquiry = createServerFn({ method: "POST" })
  .validator((data: unknown) => inquirySchema.parse(data))
  .handler(async ({ data }) => {
    console.log("[inquiry] Received new inquiry:", data);

    // Honeypot check
    if (data.website && data.website.trim() !== "") {
      console.log("[inquiry] Honeypot triggered, silently dropping");
      return { ok: true, inquiryId: crypto.randomUUID(), emailSent: false };
    }

    if (!sesClient) {
      sesClient = new SESClient({
        // Use custom non‑reserved env vars for Amplify
        region: process.env.SES_REGION || "us-east-1",
        credentials: {
          accessKeyId: process.env.SES_ACCESS_KEY_ID || "",
          secretAccessKey: process.env.SES_SECRET_ACCESS_KEY || "",
        },
      });
    }

    const inquiryId = crypto.randomUUID();

    // Construct HTML email body
    const itemsHtml = data.items
      .map((item, index) => {
        const bgColor = index % 2 === 0 ? "" : "background-color: #fafafa;";
        return `
                  <tr style="${bgColor}">
                    <td style="border-bottom: 1px solid #E0E0E0; color: #444444; font-size: 13px; line-height: 1.4;">
                      <strong>${item.productName}</strong><br>
                      <span style="color: #888888; font-size: 11px;">Handle: ${item.productHandle}</span>
                    </td>
                    <td style="border-bottom: 1px solid #E0E0E0; color: #444444; font-size: 13px;">${item.categoryName || "N/A"}</td>
                    <td align="center" style="border-bottom: 1px solid #E0E0E0; color: #444444; font-size: 13px; font-weight: bold;">${item.quantity}</td>
                    <td style="border-bottom: 1px solid #E0E0E0; color: #444444; font-size: 13px;">${item.note || "None"}</td>
                  </tr>`;
      })
      .join("");

    const htmlBody = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>QT Food Wholesale Inquiry</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; background-color: #f7f7f7; -webkit-font-smoothing: antialiased;">
  <table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #f7f7f7; padding: 40px 20px;">
    <tr>
      <td align="center">
        <!-- Main Email Container -->
        <table width="100%" border="0" cellspacing="0" cellpadding="0" style="max-width: 600px; background-color: #ffffff; border: 1px solid #E0E0E0; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 10px rgba(0,0,0,0.05);">
          
          <!-- Header Section -->
          <tr>
            <td align="center" style="padding: 40px 20px 20px 20px; background-color: #ffffff;">
              <img src="https://www.qtfoods.co.jp/assets/images/logo.png" alt="QT Food Logo" style="display: block; max-width: 150px; height: auto; border: 0;">
            </td>
          </tr>
          <tr>
            <td align="center" style="background-color: #2E5A2E; padding: 15px 20px;">
              <h2 style="margin: 0; color: #ffffff; font-size: 20px; font-weight: normal; letter-spacing: 0.5px;">New Wholesale Inquiry Received</h2>
            </td>
          </tr>

          <!-- Customer Details Section -->
          <tr>
            <td style="padding: 40px 30px 20px 30px;">
              <h3 style="margin: 0 0 15px 0; color: #2E5A2E; font-size: 16px; text-transform: uppercase; letter-spacing: 1px; border-bottom: 2px solid #2E5A2E; padding-bottom: 8px; display: inline-block;">Customer Details</h3>
              <table width="100%" border="0" cellspacing="0" cellpadding="0" style="margin-top: 10px;">
                <tr>
                  <td width="35%" style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #666666; font-size: 14px; font-weight: bold;">Company</td>
                  <td width="65%" style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #333333; font-size: 14px;">${data.company}</td>
                </tr>
                <tr>
                  <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #666666; font-size: 14px; font-weight: bold;">Contact Name</td>
                  <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #333333; font-size: 14px;">${data.contactName}</td>
                </tr>
                <tr>
                  <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #666666; font-size: 14px; font-weight: bold;">Email</td>
                  <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #333333; font-size: 14px;">
                    <a href="mailto:${data.email}" style="color: #2E5A2E; text-decoration: none;">${data.email}</a>
                  </td>
                </tr>
                <tr>
                  <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #666666; font-size: 14px; font-weight: bold;">Phone</td>
                  <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #333333; font-size: 14px;">${data.phone || "N/A"}</td>
                </tr>
                <tr>
                  <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #666666; font-size: 14px; font-weight: bold;">Country</td>
                  <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #333333; font-size: 14px;">${data.country || "N/A"}</td>
                </tr>
                <tr>
                  <td valign="top" style="padding: 10px 0; color: #666666; font-size: 14px; font-weight: bold;">Message</td>
                  <td style="padding: 10px 0; color: #333333; font-size: 14px; line-height: 1.5;">${data.message ? data.message.replace(/\\n/g, "<br/>") : "None"}</td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Order Line Items Section -->
          <tr>
            <td style="padding: 20px 30px 40px 30px;">
              <h3 style="margin: 0 0 20px 0; color: #2E5A2E; font-size: 16px; text-transform: uppercase; letter-spacing: 1px; border-bottom: 2px solid #2E5A2E; padding-bottom: 8px; display: inline-block;">Requested Items</h3>
              <table width="100%" border="0" cellspacing="0" cellpadding="12" style="border-collapse: collapse; border: 1px solid #E0E0E0; margin-top: 5px;">
                <thead>
                  <tr>
                    <th align="left" style="background-color: #f9f9f9; border-bottom: 2px solid #E0E0E0; color: #333333; font-size: 13px; font-weight: bold; padding: 12px;">Product</th>
                    <th align="left" style="background-color: #f9f9f9; border-bottom: 2px solid #E0E0E0; color: #333333; font-size: 13px; font-weight: bold; padding: 12px;">Category</th>
                    <th align="center" style="background-color: #f9f9f9; border-bottom: 2px solid #E0E0E0; color: #333333; font-size: 13px; font-weight: bold; padding: 12px;">Qty</th>
                    <th align="left" style="background-color: #f9f9f9; border-bottom: 2px solid #E0E0E0; color: #333333; font-size: 13px; font-weight: bold; padding: 12px;">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  ${itemsHtml}
                </tbody>
              </table>
            </td>
          </tr>

          <!-- Footer Section -->
          <tr>
            <td align="center" style="padding: 30px; background-color: #fdfdfd; border-top: 1px solid #E0E0E0;">
              <table width="100%" border="0" cellspacing="0" cellpadding="0">
                <tr>
                  <td align="center" style="color: #888888; font-size: 12px; line-height: 1.6;">
                    <strong>QT FOODS CO., LTD.</strong><br>
                    2-12-5, IZUMI EBINA-SHI, KANAGAWA-KEN,<br>
                    243-0437, JAPAN<br>
                    Tel: +81-46-236-1444 | Fax: +81-46-236-1565<br>
                    Email: <a href="mailto:sales@qtfoods.co.jp" style="color: #2E5A2E; text-decoration: none;">sales@qtfoods.co.jp</a><br>
                    <a href="https://qtfoods.co.jp" style="color: #2E5A2E; text-decoration: none;">https://qtfoods.co.jp</a>
                  </td>
                </tr>
                <tr>
                  <td align="center" style="padding-top: 20px; color: #aaaaaa; font-size: 11px; font-style: italic;">
                    This is an automated notification from QT Food. Please do not reply to this email.
                  </td>
                </tr>
              </table>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

    let emailSent = false;
    try {
      const command = new SendEmailCommand({
        Source: INQUIRY_SENDER,
        Destination: {
          ToAddresses: INQUIRY_RECIPIENTS,
          CcAddresses: [data.email],
        },
        ReplyToAddresses: [data.email],
        Message: {
          Subject: { Data: `New Inquiry from ${data.company}` },
          Body: {
            Html: { Data: htmlBody },
            Text: { Data: `New Inquiry from ${data.company}. Please view HTML version.` },
          },
        },
      });

      const response = await sesClient!.send(command);
      console.log("[inquiry] Email sent via SES", response.MessageId);
      emailSent = true;
    } catch (e) {
      console.error("[inquiry] SES email send failed", e);
    }

    return { ok: true, inquiryId, emailSent };
  });
